<template>
    <div class="row relative">
        <div class="col w-full md:w-1/2">
            <!-- Vehicle Registration -->
            <div class="quote-form__field-container">
                <div class="quote-form__field">
                    <label for="quotation-vehicleRegistration">Vehicle Registration *</label>
                    <input id="quotation-vehicleRegistration" name="quotation[vehicleRegistration]" type="text"
                        :value="vehicleRegistration" @input="vehicleRegistration = $event.target.value.toUpperCase()"
                        @keydown.enter.stop.prevent="findVehicle">
                </div>
                <p v-if="vehicleRegistrationErrorMessage" class="quote-form__field-error">{{
                        vehicleRegistrationErrorMessage
                }}</p>
                <p v-else-if="ajaxErrorMessage" class="quote-form__field-error">{{ ajaxErrorMessage }}</p>
            </div>

            <!-- Find Vehicle Button -->
            <div v-if="isFindVehicleButtonVisible">
                <p v-if="canMakeVehicleApiRequest" class="text-right">
                    <button type="button"
                        class="text-orange hover:text-orange-dark focus:text-orange-dark focus:outline-none focus:underline"
                        :class="{ 'cursor-not-allowed opacity-50': !vehicleRegistration }"
                        :disabled="!vehicleRegistration" @click.prevent="findVehicle">Find my vehicle <i
                            class="fas fa-car ml-1"></i></button>
                </p>
            </div>

            <!-- Vehicle Make -->
            <div class="quote-form__field-container">
                <div class="quote-form__field" v-if="brands">
                    <div class="relative">
                        <label for="quotation-vehicleMake">Vehicle Make *</label>
                        <div class="select">
                            <select id="quotation-vehicleMake" name="quotation[vehicleMake]"
                                class="quote-form__field-container" v-model="vehicleMake">
                                <option disabled value :selected="!make">Choose</option>
                                <option v-for="brand, id in brands" :key="id" :value="id">{{ brand }}</option>
                            </select>
                        </div>
                    </div>
                    <p v-if="vehicleMakeErrorMessage" class="quote-form__field-error">{{ vehicleMakeErrorMessage }}</p>
                </div>
            </div>

            <!-- Vehicle Model -->
            <div class="quote-form__field-container">
                <div class="quote-form__field">
                    <label for="quotation-vehicleModel">Vehicle Model *</label>
                    <input id="quotation-vehicleModel" name="quotation[vehicleModel]" type="text"
                        v-model="vehicleModel">
                </div>
                <p v-if="vehicleModelErrorMessage" class="quote-form__field-error">{{ vehicleModelErrorMessage }}</p>
            </div>
        </div>

        <!-- Vehicle Image -->
        <div class="col w-full md:w-1/2 flex justify-center items-center">
            <img v-if="vehicleImage" class="quote-form__vehicle-img"
                :class="{ 'max-w-[60%] md:max-w-full': isUsingPlaceholderImage }" :src="vehicleImage"
                :alt="registration" />
        </div>

        <!-- Continue Button -->
        <div class="col w-full mt-8">
            <div class="quote-form__buttons quote-form__buttons--continue-only">
                <FormButton class="button button--chevron transition-none" :disabled="isInvalid">
                    Continue
                    <IconChevron />
                </FormButton>
            </div>
        </div>

        <!-- Ajax loading spinner -->
        <div v-show="ajaxIsLoading"
            class="bg-white/90 flex justify-center items-center w-full h-full absolute top-0 left-0 z-10">
            <span class="is-loading"></span>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import * as yup from 'yup'
import { useField } from 'vee-validate'
import FormButton from '@/components/FormButton.vue'
import IconChevron from '@/components/icons/IconChevron.vue'
import AjaxGetVehicleData from '@/services/AjaxGetVehicleData'

const props = defineProps({
    registration: {
        type: String,
        required: true
    },
    make: {
        type: String,
        required: true
    },
    brands: {
        type: Object,
        required: true
    },
    model: {
        type: String,
        required: true
    },
    image: {
        type: String,
        required: true
    },
    fallbackImage: {
        type: String,
        default: ''
    },
    showErrorMessageOnLoad: {
        type: Boolean,
        default: false
    }
})

// Fields
const { value: vehicleRegistration, errorMessage: vehicleRegistrationErrorMessage, meta: vehicleRegistrationMeta } = useField('quotation[vehicleRegistration]', yup.string().required().max(10).label('Vehicle Registration'), { initialValue: props.registration })
const { value: vehicleMake, errorMessage: vehicleMakeErrorMessage, meta: vehicleMakeMeta } = useField('quotation[vehicleMake]', yup.string().required().label('Vehicle Make'), { initialValue: props.make })
const { value: vehicleModel, errorMessage: vehicleModelErrorMessage, meta: vehicleModelMeta } = useField('quotation[vehicleModel]', yup.string().required().label('Vehicle Model'), { initialValue: props.model })
const isInvalid = computed(() => !vehicleRegistrationMeta.valid || !vehicleMakeMeta.valid || !vehicleModelMeta.valid)

// Ajax
const ajaxIsLoading = ref(false)
const ajaxErrorMessage = ref(props.showErrorMessageOnLoad ? 'Vehicle not found. Please make sure the vehicle registration is correct or enter manually.' : '')

// Misc
const isFindVehicleButtonVisible = ref((props.registration && props.make && props.model) ? false : true)
const canMakeVehicleApiRequest = computed(() => AjaxGetVehicleData ? true : false)

// Vehicle Image
const vehicleImage = ref(props.image)
const isUsingPlaceholderImage = computed(() => vehicleImage.value.indexOf('placeholder') !== -1 ? true : false)

async function findVehicle() {
    if (AjaxGetVehicleData) {
        ajaxIsLoading.value = true
        const formData = new FormData()
        const vrm = vehicleRegistration.value ? vehicleRegistration.value.replace(/ /g, '').toUpperCase() : ''
        formData.append("strVRM", vrm)

        try {
            const { data, status } = await AjaxGetVehicleData.post('/', formData)
            if (data && (status >= 200 && status < 300)) {
                const { body: vehicleData } = data
                if (vehicleData && vehicleData.hasOwnProperty('Combined_Model') && vehicleData.hasOwnProperty('Combined_Make')) {
                    // Success!
                    const { Combined_Model: model, Combined_Make: make, VehicleImageUrl: image } = vehicleData

                    // Set the image if there is one and the fallback image that is passed down as a prop if there isn't.
                    let hasImage = false
                    if (image && image.length > 0) {
                        hasImage = image[0].replace(/\s/g, '') ? true : false
                    }

                    populateFieldsWithVehicleData(
                        vehicleData.Combined_Model,
                        vehicleData.Combined_Make,
                        hasImage ? vehicleData['@attributes']?.FullVehicleImageUrl : props.fallbackImage
                    )
                    ajaxErrorMessage.value = '';
                    isFindVehicleButtonVisible.value = false
                } else {
                    throw 'Vehicle not found. Please make sure the vehicle registration is correct or enter manually.'
                }
            } else {
                throw 'Error. Please enter vehicle details manually.'
            }
        } catch (errorMessage) {
            ajaxErrorMessage.value = errorMessage
        }
        ajaxIsLoading.value = false
    }
}

function populateFieldsWithVehicleData(model = null, make = null, imgUrl = null) {
    // MODEL. The below chain of string methods is to simply capitalise the string
    if (model) {
        vehicleModel.value = model.toLowerCase().replace(/\w\S*/g, (w) => (w.replace(/^\w/, (c) => c.toUpperCase())))
    }

    // MAKE.
    if (make && props.brands) {
        for (let id in props.brands) {
            if (make.toLowerCase() === props.brands[id].toLowerCase()) {
                vehicleMake.value = id
            }
        }
    }

    // IMAGE.
    if (imgUrl) {
        vehicleImage.value = imgUrl
    }
}

watch(
    () => vehicleRegistration.value,
    (newVal, oldVal) => {
        if (ajaxErrorMessage.value && newVal !== oldVal) ajaxErrorMessage.value = ''
        if (newVal !== oldVal) isFindVehicleButtonVisible.value = true
    }
);
</script>

<style lang="scss" scoped>
#quotation-vehicleMake {
    direction: ltr;

    @screen sm {
        direction: rtl;
    }
}
</style>